import React, { Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import NavigationDots from './navigation-dots';
import {
  useEventListeners,
  useClientValues,
  useOnSwipe,
  useOffset
} from './hooks';

const OutterWrapper = styled.div`
  height: 32rem;
  position: relative;

  @media (min-width: ${props => props.theme.breakpoints.md}px) {
    height: 31rem;
  }
`;

const InnerWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const CenterItems = css`
  left: 50%;
  transform: translateX(-50%);
`;

const ChildrenWrapper = styled.div`
  position: absolute;
  margin: auto 0;
  display: flex;
  transform: translate3d(${props => props.leftOffset}px, 0px, 0px);
  animation: ${props => props.animation}
    ${props => (props.showAllItems ? 0 : 1000)}ms
    cubic-bezier(0.215, 0.61, 0.355, 1);
  touch-action: none;
  height: 100%;

  ${props => props.showAllItems && CenterItems};
`;

const Carousel = ({
  children,
  currentItemIndex,
  onSwipeComplete,
  onLeftSwipe,
  onRightSwipe,
  onNavigationDotClick,
  hasSelectedItem
}) => {
  const carouselRef = useRef(null);
  const childrenRef = useRef(null);
  const childrenLength = childrenRef?.current?.childElementCount;
  const [eventData, setEventData] = useClientValues();
  useEventListeners(carouselRef, setEventData);
  const [currentItem, setCurrentItem] = useOnSwipe({
    eventData,
    currentItemIndex,
    childrenLength,
    onSwipeComplete,
    onLeftSwipe,
    onRightSwipe
  });
  const offset = useOffset(carouselRef, childrenRef, currentItem);
  const slideAnimation = keyframes`
  from {
    transform: translate3d(${offset.previous}px, 0px, 0px);
  }
  to {
    transform: translate3d(${offset.current}px, 0px, 0px);
  }
`;

  return (
    <Fragment>
      <OutterWrapper
        ref={carouselRef}
        isSwiping={eventData.isSwiping}
        hasSelectedItem={hasSelectedItem}
      >
        <InnerWrapper>
          <ChildrenWrapper
            showAllItems={offset.showAllItems}
            ref={childrenRef}
            leftOffset={offset.current}
            previousLeftOffset={offset.previous}
            animation={slideAnimation}
          >
            {children}
          </ChildrenWrapper>
        </InnerWrapper>
      </OutterWrapper>

      {!offset.showAllItems && (
        <NavigationDots
          count={childrenLength}
          currentItem={currentItem}
          onClick={item => {
            if (onNavigationDotClick) onNavigationDotClick({ item });

            setCurrentItem(item);
          }}
        />
      )}
    </Fragment>
  );
};

Carousel.propTypes = {
  children: PropTypes.node.isRequired,
  hasSelectedItem: PropTypes.bool.isRequired,
  currentItemIndex: PropTypes.number,
  onNavigationDotClick: PropTypes.func,
  onSwipeComplete: PropTypes.func,
  onLeftSwipe: PropTypes.func,
  onRightSwipe: PropTypes.func
};

Carousel.defaultProps = {
  currentItemIndex: 0,
  onNavigationDotClick: null,
  onSwipeComplete: null,
  onLeftSwipe: null,
  onRightSwipe: null
};

export default Carousel;
